import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { CatalogPageModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface MemberList {
    fields: {
        heading: string;
        content: string;
        categoryFields: any;
        brandFields: any;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location?.pathname?.toLowerCase().startsWith("/content/") ? "" : location?.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    return {
        productsDataView: getProductListDataView(state),
        catalogPageState,
        categoriesDataView: getCategoriesDataView(state),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

const enum fields {
    content = "content",
    divisions = "divisions",
    categoryFields = "categoryFields",
    brandFields = "brandFields",
    heading = "heading",
    backgroundImage = "backgroundImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.divisions]: MemberList[];
        [fields.content]: string;
        [fields.heading]: string;
        [fields.backgroundImage]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProduct & CatalogPageModel & HasProductContext;

export interface SearchProductItemStyle {
    mainContainer?: GridItemProps;
    titleWrapper?: InjectableCss;
    contentStyles?: InjectableCss;
    downloadPdfWrapper?: InjectableCss;
    backgroundImageStyles?: ImgProps;
    overlayStyles: InjectableCss;
}

export const searchProductItemStyle: SearchProductItemStyle = {
    backgroundImageStyles: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            object-fit: cover;
            width: 100%;
            display: block;
            height: 100%;
        `,
    },
    overlayStyles: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            mix-blend-mode: overlay;
            background-color: rgba(38, 107, 64, 0.8);
            opacity: 0.8;
        `,
    },
    mainContainer: {
        width: [12, 12, 12, 6, 6],
        css: css`
            width: 100%;
            margin-top: 20px;
            position: relative;
            height: 100%;
            background: rgba(38, 107, 64, 0.8);
            padding: 32px;

            p .titleWrapper {
                color: #ffffff;
                font-weight: 600;
                line-height: 32px;
                font-size: 24px;
                margin-top: 4px;
            }

            p .contentStyles {
                color: #ffffff;
                font-weight: 400;
                font-size: 14px;
                line-height: 20px;
                margin-top: 24px;
                p {
                    font-size: 14px;
                }
            }

            .downloadPdfWrapper {
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                margin-top: 24px;
                align-items: center;
                padding: 8px 16px;
                background: #ffffff;
                border-radius: 16px;
                position: relative;
                z-index: 2;
                opacity: 1;
                span {
                    color: ${getColor("secondary")};
                }
            }

            .headingStyles {
                color: #ffffff !important;
                font-size: 12px;
                line-height: 16px;
                text-transform: uppercase;
            }
        `,
    },
};

const styles = searchProductItemStyle;

export const CENPdpAdvertisement: FC<Props> = ({ fields, productContext, catalogPageState }) => {
    const productBrandName = productContext.product?.brand?.name;

    const brandResult = fields.divisions.find(obj =>
        obj.fields.brandFields
            .split(",")
            .map((brand?: string) => brand?.trim().toLowerCase())
            .includes(productBrandName?.toLowerCase()),
    );

    const breadcrumbsData = catalogPageState.value?.breadCrumbs?.map(item => item.text);

    const categoriesArray = breadcrumbsData?.slice(1, -1);

    const lastCategory = categoriesArray?.[categoriesArray.length - 1]?.toLowerCase();

    const categoriesResult = fields.divisions.find(categoryObj =>
        categoryObj.fields.categoryFields
            .split(",")
            .map((category: string) => category?.trim().toLowerCase())
            .includes(lastCategory),
    );
    if (!brandResult && !categoriesResult) {
        return null;
    }

    const matchedBrandOrCategory = brandResult?.fields ?? categoriesResult?.fields;

    return (
        <StyledWrapper {...styles.mainContainer}>
            <Img src={fields.backgroundImage} altText={fields?.heading || 'Advertisement'} {...styles.backgroundImageStyles}></Img>
            <StyledWrapper {...styles.overlayStyles}></StyledWrapper>
            <StyledWrapper>
                {matchedBrandOrCategory && (
                    <Typography as="p">
                        {parse(transformContentHtml(matchedBrandOrCategory.content), parserOptions)}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProductContext(CENPdpAdvertisement)),
    definition: {
        group: "Basic",
        displayName: "CEN PDP Advertisment",
        fieldDefinitions: [
            {
                name: fields.backgroundImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
            },
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.heading;
                },
                defaultValue: [],
                fieldType: "General",
                displayName: "Advertisement",
                fieldDefinitions: [
                    {
                        name: fields.brandFields,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        displayName: "Brand Keywords",
                    },
                    {
                        name: fields.categoryFields,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        displayName: "Category Keywords",
                    },
                    {
                        name: fields.heading,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },

                    {
                        name: fields.content,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
